import { Flex, Grid, Heading, Image } from "@chakra-ui/react"
import { Button } from "@material-ui/core"
import ChakraInput from "components/Misc/FormComponents/ChakraInput"
import ChakraTextArea from "components/Misc/FormComponents/ChakraTextArea"
import { apiLink } from "config"
import { Field, FieldArray, useFormikContext } from "formik"
import React from "react"
import CheckboxFormField from "../Misc/FormComponents/CheckboxFormField"
import FileUpload from "../Misc/FormComponents/FileUpload"
import axios from "axios"
import AsyncSelect from "react-select/async"

type QuestionOption = {
  label: string
  value: number
  question: any
}

const QuizzesSubForm: React.FC<{
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  validateForm: any
  quizzes: any[]
}> = ({ setFieldValue, validateForm }) => {
  const { values } = useFormikContext<any>()

  const loadQuestionCodes = async (inputValue: string) => {
    if (!inputValue) return []

    const res = await axios.get(
      `${apiLink}/questions?Code_contains=${inputValue}`
    )

    return res.data.map((q: any) => ({
      label: q.Code,
      value: q.id,
      question: q
    }))
  }

  const handleSelect = (option: QuestionOption | null, index: number) => {
    if (!option) return

    const q = option.question

    setFieldValue(`quizzes.${index}.question`, "")
    setFieldValue(`quizzes.${index}.sinhalaText`, "")
    setFieldValue(`quizzes.${index}.isTrue`, false)
    setFieldValue(`quizzes.${index}.image`, "")
    setFieldValue(`quizzes.${index}.italianVoiceRecord`, "")
    setFieldValue(`quizzes.${index}.sinhalaVoiceRecord`, "")

    setFieldValue(`quizzes.${index}.questionCode`, q.Code)
    setFieldValue(`quizzes.${index}.question`, q.ItalianText || "")
    setFieldValue(`quizzes.${index}.sinhalaText`, q.SinhalaText || "")
    setFieldValue(`quizzes.${index}.isTrue`, q.Answer === true)

    setFieldValue(`quizzes.${index}.image`, q.Image || "")
    setFieldValue(
      `quizzes.${index}.italianVoiceRecord`,
      q.ItalianVoiceRecord || ""
    )
    setFieldValue(
      `quizzes.${index}.sinhalaVoiceRecord`,
      q.SinhalaVoiceRecord || ""
    )
  }

  return (
    <FieldArray name="quizzes">
      {(arrayHelpers) => (
        <div>
          <Heading fontSize="lg" color="gray.700" fontWeight="medium">
            Quizzes
          </Heading>

          {values.quizzes.map((quiz: any, index: number) => (
            <Flex
              key={index}
              border="1px solid gray"
              flexDir="column"
              width="100%"
              p={2}
              m={1}
              borderRadius={5}
            >
              <AsyncSelect
                cacheOptions
                loadOptions={loadQuestionCodes}
                defaultOptions={false}
                placeholder="Type Question Code"
                onChange={(option) =>
                  handleSelect(option as QuestionOption, index)
                }
                formatOptionLabel={(option: QuestionOption) => {
                  const q = option.question

                  return (
                    <Grid
                      templateColumns="40px 80px 1fr 1fr 80px"
                      gap={3}
                      alignItems="center"
                      width="100%"
                    >
                      <div>
                        {q.Image && (
                          <Image
                            src={`${apiLink}${q.Image}`}
                            boxSize="35px"
                            objectFit="cover"
                            borderRadius="4px"
                          />
                        )}
                      </div>

                      <div>
                        <b>{q.Code}</b>
                      </div>

                      <div style={{ fontSize: "13px" }}>{q.ItalianText}</div>

                      <div style={{ fontSize: "13px", color: "#555" }}>
                        {q.SinhalaText}
                      </div>

                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: q.Answer ? "green" : "red"
                        }}
                      >
                        {q.Answer ? "True" : "False"}
                      </div>
                    </Grid>
                  )
                }}
              />

              <Grid
                gridTemplateColumns={{
                  base: "1fr",
                  md: "0.5fr 2fr 2fr 0.5fr"
                }}
                gap="4"
                mt={3}
              >
                <Field
                  label="Quiz No"
                  component={ChakraInput}
                  name={`quizzes.${index}.qNo`}
                />

                <Field
                  label="Question"
                  component={ChakraTextArea}
                  name={`quizzes.${index}.question`}
                  multiline
                />

                <Field
                  label="Sinhala Text"
                  component={ChakraTextArea}
                  name={`quizzes.${index}.sinhalaText`}
                  multiline
                />

                <Field
                  label="True Or False"
                  component={CheckboxFormField}
                  name={`quizzes.${index}.isTrue`}
                />
              </Grid>

              <Grid
                gridTemplateColumns={{
                  base: "1fr",
                  md: "0.2fr 2fr 0.5fr 0.5fr"
                }}
                alignItems="center"
                gap="4"
                mt={3}
              >
                <Flex direction="column" alignItems="center">
                  {quiz.image && (
                    <Image
                      src={`${apiLink}${quiz.image}`}
                      height="50px"
                      width="50px"
                      objectFit="cover"
                    />
                  )}
                  <FileUpload
                    text="Upload Image"
                    handleSetFieldValue={setFieldValue}
                    handleFieldName={`quizzes.${index}.image`}
                  />
                </Flex>

                <Flex direction="column" alignItems="center">
                  {quiz.italianVoiceRecord && (
                    <audio controls>
                      <source src={`${apiLink}${quiz.italianVoiceRecord}`} />
                    </audio>
                  )}
                  <FileUpload
                    text="Upload Italian Voice Record"
                    handleSetFieldValue={setFieldValue}
                    handleFieldName={`quizzes.${index}.italianVoiceRecord`}
                    acceptFileTypes="audio/mpeg,audio/x-m4a"
                  />
                </Flex>

                <Flex direction="column" alignItems="center">
                  {quiz.sinhalaVoiceRecord && (
                    <audio controls>
                      <source src={`${apiLink}${quiz.sinhalaVoiceRecord}`} />
                    </audio>
                  )}
                  <FileUpload
                    text="Upload Sinhala Voice Record"
                    handleSetFieldValue={setFieldValue}
                    handleFieldName={`quizzes.${index}.sinhalaVoiceRecord`}
                    acceptFileTypes="audio/mpeg,audio/x-m4a"
                  />
                </Flex>
              </Grid>

              <Flex justifyContent="center" mt={2}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    arrayHelpers.remove(index)
                    setTimeout(() => validateForm(), 200)
                  }}
                >
                  -
                </Button>

                <Button
                  color="primary"
                  variant="contained"
                  onClick={() =>
                    arrayHelpers.insert(index + 1, {
                      questionCode: "",
                      qNo: "",
                      question: "",
                      isTrue: false,
                      image: "",
                      italianVoiceRecord: "",
                      sinhalaVoiceRecord: "",
                      sinhalaText: ""
                    })
                  }
                >
                  +
                </Button>
              </Flex>
            </Flex>
          ))}
        </div>
      )}
    </FieldArray>
  )
}

export default QuizzesSubForm
